import * as React from "react";
import type { BuyerSendKind, BuyerSendVersionDto } from "@/api/buyerSendVersions";
import { EmptyState, Skeleton } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import {
  BROKER_INTRO_HISTORY_CAPTION,
  IntroEmailVersionTable,
} from "./IntroEmailVersionTable";
import { PackageVersionCards } from "./PackageVersionCards";
import { SendVersionDetail } from "./SendVersionDetail";

/** The sr-only caption for the Broker intro tab's text, web-form and follow-up
 *  histories — not "intro emails", which they are not. */
const BROKER_OUTREACH_HISTORY_CAPTION =
  "Messages sent to the listing broker, newest first. Select a version to read it.";

/**
 * Empty-state copy per kind.
 *
 * 🔴 An EXHAUSTIVE Record over `BuyerSendKind`, so a new send kind is a
 * typecheck failure here rather than an empty state describing the wrong
 * recipient. The two package kinds are the SAME document to opposite sides of
 * the transaction, and their copy is what tells them apart on screen.
 */
const EMPTY_COPY: Record<BuyerSendKind, { title: string; description: string }> = {
  Package: {
    title: "No package has been sent to the listing broker yet",
    description:
      "Every package that reaches a listing broker is recorded here, with the letter it enclosed.",
  },
  PackageToBuyer: {
    title: "No package has been sent to the buyer yet",
    description:
      "Every time this package goes to the buyer it is recorded here. Sending it to them is separate from sending it to the listing broker — either can happen first, and neither requires the other.",
  },
  IntroEmail: {
    title: "No intro email has been sent yet",
    description:
      "Every intro email that reaches the buyer is recorded here, with what it said.",
  },
  BrokerIntro: {
    title: "No broker intro has been sent yet",
    description:
      "Every introduction that reaches a listing broker is recorded here, with what it said. It is the message that asks whether they want the package, so it comes before any package is sent.",
  },
  BrokerText: {
    title: "No text has been logged yet",
    description: "Every text to the listing broker that is logged from the Broker intro tab is recorded here, with what it said.",
  },
  BrokerWebForm: {
    title: "No web form message has been logged yet",
    description: "Every message submitted through the listing broker's contact form is recorded here, with what it said.",
  },
  BrokerEmailFU: {
    title: "No follow-up email has been sent yet",
    description: "Every follow-up email to the listing broker is recorded here, with what it said.",
  },
  BrokerTextFU: {
    title: "No follow-up text has been logged yet",
    description: "Every follow-up text to the listing broker is recorded here, with what it said.",
  },
};

/**
 * The send history of one kind, as master and detail: the list of versions on
 * the left, the selected version rendered on the right.
 *
 * <p><b>What earns a row.</b> A version row IS a send. A draft that was
 * generated and never sent has none; a regenerated draft that replaced an unsent
 * one has none; the Redraft undo — which keeps the immediately previous draft
 * recoverable — has none. The table answers exactly one question, "what did the
 * recipient actually receive, and when", and a row for something nobody received
 * would make it answer that question wrongly. The current DRAFT sits above this
 * component, in the panel that owns it.</p>
 *
 * <p><b>No nesting and no navigation.</b> Two versions are one click apart —
 * a reader comparing what they said in March with what they said in June should
 * not be opening and closing anything, and should never leave the record to do
 * it.</p>
 *
 * <p>🔴 <b>THE LAYOUT RESPONDS TO ITS CONTAINER, NEVER THE VIEWPORT.</b> This
 * body renders in a record drawer AND on a full page, and a viewport breakpoint
 * (`lg:grid-cols-[20rem_1fr]`) is correct in exactly one of them: inside a
 * drawer on a wide monitor the viewport is still `lg`, so a page-width split
 * stays in a box nowhere near page width and the list column crushes the
 * preview. The split here is a WRAPPING FLEX ROW, which wraps on the width of
 * the flex container itself:</p>
 *
 * <ul>
 *   <li><b>Narrow container</b> — the two children cannot both meet their
 *       flex-basis, so they wrap: list above, preview below, both full width.
 *       This is the drawer at its narrowest and the page on a small screen.</li>
 *   <li><b>Comfortable container (~1000px)</b> — a 20rem list beside a preview
 *       taking the rest, which is the ~320/680 the drawer wants.</li>
 *   <li><b>Wide container (1400px+)</b> — the list stays at about 20rem and
 *       every further pixel goes to the PREVIEW, because the preview is the
 *       thing being read. A list column that grew with the window would be
 *       400px of whitespace beside dates.</li>
 * </ul>
 *
 * <p>🔴 <b>The list's basis differs by kind, and it has to.</b> The intro
 * email's list is a five-column table (§E4) and five columns do not fit in
 * 20rem — squeezed there, every column becomes a truncation. So the table
 * declares a 34rem basis: it sits beside the preview once the container can
 * afford both, and below the reader's threshold it WRAPS to full width, which
 * is a table at its most readable rather than a table crushed into a rail. The
 * package's cards are narrow by nature and take the 20rem. One component, one
 * mechanism, one number that varies — not two layouts maintained apart.</p>
 *
 * <p>The `flex-[999…]` on the preview is what makes that last part true, and it
 * is why neither child needs a `max-width`: with a grow factor three orders of
 * magnitude larger than the list's, a 1400px container hands the list under a
 * pixel of the free space and the preview the rest, so the list sits at its
 * basis without being capped there. That distinction matters once they WRAP —
 * a capped list alone on its line would leave dead space beside itself, whereas
 * this one grows to fill the line, which is what a stacked layout wants.
 * `min-w-0` on the preview is not optional either: a flex item defaults to
 * `min-width: auto`, so a long unbroken line in a body would otherwise push its
 * own track wide and take the wrap decision with it.</p>
 */
export function SendHistory({
  submissionId,
  kind,
  canWrite,
  onStartDraftFrom,
  onSendAgain,
}: {
  submissionId: string;
  kind: BuyerSendKind;
  canWrite: boolean;
  /** Load this version's body back into the draft. */
  onStartDraftFrom?: (version: BuyerSendVersionDto) => void;
  /** Send this exact version again — down the ordinary send path. */
  onSendAgain?: (version: BuyerSendVersionDto) => void;
}) {
  const { data, isLoading, isError } = useSendHistory(submissionId);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  // Exhaustive by type: a new BuyerSendKind fails typecheck here rather than
  // rendering an empty state with the wrong noun on it.

  const versions = React.useMemo(() => {
    switch (kind) {
      case "Package":
        return data?.packages ?? [];
      case "PackageToBuyer":
        return data?.packagesToBuyer ?? [];
      case "IntroEmail":
        return data?.introEmails ?? [];
      case "BrokerIntro":
        return data?.brokerIntros ?? [];
      case "BrokerText":
        return data?.brokerTexts ?? [];
      case "BrokerWebForm":
        return data?.brokerWebForms ?? [];
      case "BrokerEmailFU":
        return data?.brokerFollowUpEmails ?? [];
      case "BrokerTextFU":
        return data?.brokerFollowUpTexts ?? [];
    }
  }, [data, kind]);

  // 🔴 Both package kinds render as CARDS, because both ARE the same document —
  // a package is a document and its distinguishing facts want room. The two
  // EMAILS are tables. Deriving this once stops the two package kinds drifting
  // into different-looking histories of one artifact.
  //
  // 🔴 Stated POSITIVELY ("is this a document?") rather than as "not the intro
  // email". The negative form silently classified every future kind as a
  // package, which is how the broker intro — an email — would have arrived
  // rendering as a document card.
  const isPackage = kind === "Package" || kind === "PackageToBuyer";

  // The newest is the default selection, and it follows the data: after a send
  // appends a version the reader should be looking at what just went out, not at
  // whatever they had open before. Keyed on the id rather than on an index
  // because the list is newest-first and every index shifts on an append.
  const selected =
    versions.find((v) => v.id === selectedId) ?? versions[0] ?? null;

  if (isLoading) {
    return (
      <div className="grid gap-space-3" aria-busy="true">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // An error is not an empty record. Saying "nothing has been sent" when the
  // request failed tells a specialist their outreach vanished.
  if (isError) {
    return (
      <p className="text-small text-status-error m-0">
        The send history could not be loaded. Refresh to try again.
      </p>
    );
  }

  if (versions.length === 0) {
    return (
      <EmptyState
        title={EMPTY_COPY[kind].title}
        description={EMPTY_COPY[kind].description}
        className="py-space-5"
      />
    );
  }

  return (
    <div className="flex flex-wrap gap-space-4 items-start">
      {/* The list. No `max-w` and deliberately no breakpoint: the grow ratio
          below already holds it at its basis on a wide container, and a `max-w`
          would leave dead space beside it once the two have WRAPPED and the list
          is alone on its line.

          The basis literals are spelled out rather than interpolated — Tailwind
          only emits class names it can find verbatim in the source. */}
      <div
        className={cn(
          "min-w-0",
          isPackage ? "flex-[1_1_20rem]" : "flex-[1_1_34rem]",
        )}
      >
        {isPackage ? (
          <PackageVersionCards
            versions={versions}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
          />
        ) : (
          <IntroEmailVersionTable
            versions={versions}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
            /* The visible heading above this table already names the audience;
               the caption is what a screen reader gets INSTEAD of it, so the
               two have to agree. Keyed on the kind rather than on `isPackage`,
               which cannot tell the two emails apart. */
            caption={
              kind === "IntroEmail"
                ? undefined
                : kind === "BrokerIntro"
                  ? BROKER_INTRO_HISTORY_CAPTION
                  : BROKER_OUTREACH_HISTORY_CAPTION
            }
          />
        )}
      </div>

      {/* The preview. `flex-[999…]` sends free space here; `min-w-0` stops a
          long unbroken line taking the wrap decision. */}
      <div className="flex-[999_1_26rem] min-w-0">
        {selected ? (
          <SendVersionDetail
            version={selected}
            canWrite={canWrite}
            isLatest={selected.id === versions[0]?.id}
            onStartDraftFrom={onStartDraftFrom}
            onSendAgain={onSendAgain}
          />
        ) : null}
      </div>
    </div>
  );
}
